import { DiscountContract, DiscountType, VoidCompReasonModel } from 'models'
import { IServiceCharge } from 'store/models/sellModels'
import Decimal from 'decimal.js'
import {
    distributeDiscountValueToLines,
    splitOrderDiscountsToLines,
} from './discountCalculation'
import { calculateOrderServiceCharge } from './serviceChargeCalculation'
import { round } from './common'
import calculateTax from './taxCalculation'
import { sumDecimal } from 'utilities/decimalsManipulate'
import { isEmpty } from 'lodash-es'
import {
    LineModelExtend,
    ICalculationResultModel,
} from './models/IOrderCalculationModel'
import { isVoidedItem } from '../orderItemUtil'

function calculateExcludeFromItemTaxAmount(
    line: LineModelExtend,
    parentLine?: LineModelExtend,
) {
    line.excludeFromItemTaxAmount = line.excludeFromItemTaxAmount.plus(
        sumDecimal(
            line.modifierLines
                .filter((a) => !isEmpty(a.tax))
                .map((a) => a.subtotal),
        )
            .mul(line.lineAmount)
            .mul(parentLine?.lineAmount ?? 1),
    )

    line.comboItems.forEach((combo) => {
        line.excludeFromItemTaxAmount = isEmpty(combo.tax)
            ? line.excludeFromItemTaxAmount.plus(combo.excludeFromItemTaxAmount)
            : line.excludeFromItemTaxAmount.plus(
                  combo.subtotal.mul(line.lineAmount),
              )
    })
}

function calculateLineTax(
    line: LineModelExtend,
    parentLineQuantity: number = 1,
) {
    const productValue = line.subtotal
        .mul(parentLineQuantity)
        .minus(line.excludeFromItemTaxAmount)
        .minus(line?.orderDiscountAmount ?? 0)
        .minus(line.itemDiscountAmount ?? 0)
        .minus(line.lineCompDiscount ?? 0)
    line.NetSubtotal = productValue
    line.itemTaxAmount = calculateTax(
        productValue,
        line.tax?.rate,
        line.isTaxInclusive,
    )

    let totalTax = line.itemTaxAmount
    let taxExclusive = new Decimal(0)
    let taxInclusive = new Decimal(0)
    if (line.isTaxInclusive) {
        taxInclusive = taxInclusive.plus(line.itemTaxAmount)
        line.NetSubtotal = line.NetSubtotal.minus(line.itemTaxAmount)
    } else taxExclusive = taxExclusive.plus(line.itemTaxAmount)

    line.modifierLines.forEach((modLine) => {
        const modDiscount = modLine?.orderDiscountAmount ?? new Decimal(0)
        const modTax = modLine.tax
        const modValue = modLine.subtotal
            .mul(line.amount)
            .mul(parentLineQuantity)
            .minus(modDiscount.toNumber())
        modLine.orderDiscountAmount = modDiscount
        modLine.NetSubtotal = modValue
        modLine.itemTaxAmount = new Decimal(0)
        if (!modTax) return

        modLine.itemTaxAmount = calculateTax(
            modValue,
            modLine.tax?.rate,
            modLine.isTaxInclusive,
        )

        totalTax = totalTax.plus(modLine.itemTaxAmount)
        if (modLine.isTaxInclusive) {
            taxInclusive = taxInclusive.plus(modLine.itemTaxAmount)
            modLine.NetSubtotal = modLine.NetSubtotal.minus(
                modLine.itemTaxAmount,
            )
        } else taxExclusive = taxExclusive.plus(modLine.itemTaxAmount)
    })

    line.comboItems.forEach((cl) => {
        totalTax = totalTax.plus(cl.taxAmount ?? new Decimal(0))
        taxInclusive = taxInclusive.plus(cl.taxInclusive ?? new Decimal(0))
        taxExclusive = taxExclusive.plus(cl.taxExclusive ?? new Decimal(0))
    })

    line.taxAmount = totalTax
    line.taxExclusive = taxExclusive.toNumber()
    line.taxInclusive = taxInclusive.toNumber()
}

function summarizeOrderLineTax(itemLine: LineModelExtend) {
    itemLine.comboItems.forEach((c) => {
        calculateExcludeFromItemTaxAmount(c, itemLine)
    })
    calculateExcludeFromItemTaxAmount(itemLine)

    applyDiscountToProduct(itemLine, itemLine.comboItems)

    itemLine.comboItems.forEach((comboItemLine) => {
        calculateLineTax(comboItemLine, itemLine.lineAmount)
    })
    calculateLineTax(itemLine)
}
/**
 * calculate order line tax and order totaldue
 * totaltax, service charge, order discount
 * @param orderLines
 * @param serviceCharge
 * @param entireDiscounts
 * @param entireCompDiscount
 * @param isCalculateCompAfterDiscount
 * @returns
 */
function calculateOrder(
    orderLines: LineModelExtend[],
    serviceCharge: IServiceCharge,
    entireDiscounts: DiscountContract[] = [],
    entireCompDiscount: VoidCompReasonModel | undefined = undefined,
    isCalculateCompAfterDiscount: boolean,
): ICalculationResultModel {
    // NOTE: Negative Priced Lines should not be included in order calculation
    let itemLines = orderLines
        .filter((l) => !isVoidedItem(l))
        .filter((line) => !line.isDepositAndCreditItem)

    const orderSubtotal = sumDecimal(
        itemLines.map((a) => a.subtotal ?? new Decimal(0)),
    )

    let totalDiscount
    ;({ totalDiscount, itemLines } = summarizeOrderDiscount(
        itemLines,
        orderSubtotal,
        entireDiscounts,
        entireCompDiscount,
        isCalculateCompAfterDiscount,
    ))

    itemLines.forEach((line) => summarizeOrderLineTax(line))
    const { orderServiceChargeTax, orderServiceCharge } =
        applyOrderServiceCharge(serviceCharge, itemLines, entireCompDiscount)

    const {
        totalSubTotal,
        totalDue,
        totalTax,
        totalExclusiveTax,
        totalInclusiveTax,
    } = finalizeOrder(
        itemLines,
        orderLines.filter((line) => line.isDepositAndCreditItem ?? false),
        serviceCharge,
        orderServiceChargeTax,
        orderSubtotal,
        totalDiscount,
        orderServiceCharge,
    )

    const rlt: ICalculationResultModel = {
        taxLines: itemLines,
        subtotal: totalSubTotal.toNumber(),
        totalDiscount: totalDiscount.toNumber(),
        totalServiceCharge: orderServiceCharge.toNumber(),
        totalDue: totalDue.toNumber(),
        totalTax: totalTax.toNumber(),
        totalExclusiveTax: totalExclusiveTax.toNumber(),
        totalInclusiveTax: totalInclusiveTax.toNumber(),
    }
    return rlt
}
function applyDiscountToProduct(
    itemLine: LineModelExtend,
    combosLines: LineModelExtend[],
) {
    const modLines = itemLine.modifierLines.filter(
        (a) => !!a.tax && a.subtotal.greaterThan(0),
    )
    const comboTaxLines = combosLines.filter(
        (a) =>
            !!a.tax &&
            a.subtotal
                .mul(itemLine.lineAmount)
                .minus(a.excludeFromItemTaxAmount)
                .greaterThan(0),
    )
    const totalLineDiscount = new Decimal(itemLine.lineCompDiscount ?? 0)
        .plus(itemLine.itemDiscountAmount ?? 0)
        .plus(itemLine.orderDiscountAmount)

    const noNeedToSplitedDiscount =
        (isEmpty(modLines) &&
            isEmpty(comboTaxLines) &&
            !combosLines.some((a) =>
                a.modifierLines.some((m) => !!m.tax && m.lineTotal > 0),
            )) ||
        totalLineDiscount.equals(0)
    if (noNeedToSplitedDiscount) {
        return [
            {
                ...itemLine,
                orderDiscountAmount: totalLineDiscount,
            },
        ]
    }

    //mock line for split the discount
    const splitedLines: LineModelExtend[] = []
    if (
        itemLine.subtotal
            .minus(itemLine.excludeFromItemTaxAmount)
            .greaterThan(0)
    )
        splitedLines.push({
            ...itemLine,
            subtotal: itemLine.subtotal.minus(
                itemLine.excludeFromItemTaxAmount,
            ),
        })
    comboTaxLines.forEach((comboLine) =>
        splitedLines.push({
            ...comboLine,
            subtotal: comboLine.subtotal.mul(itemLine.lineAmount),
        }),
    )
    modLines.forEach((modLine) =>
        splitedLines.push({
            ...modLine,
            subtotal: modLine.subtotal.mul(itemLine.amount),
        }),
    )
    combosLines.forEach((comboLine) =>
        comboLine.modifierLines
            .filter((a) => !!a.tax && a.lineTotal > 0)
            .forEach((comboModLine) => {
                splitedLines.push({
                    ...comboModLine,
                    subtotal: comboModLine.subtotal
                        .mul(itemLine.amount)
                        .mul(comboLine.amount),
                })
            }),
    )
    //reset line discount amount
    splitedLines.forEach((line) => {
        line.itemDiscountAmount = 0
        line.lineCompDiscount = 0
        line.orderDiscountAmount = new Decimal(0)
    })
    distributeDiscountValueToLines(
        {
            type: DiscountType.FixedAmount,
            value: totalLineDiscount.toNumber(),
        },
        itemLine.subtotal,
        splitedLines,
        false,
    )

    itemLine.orderDiscountAmount = sumDecimal(
        splitedLines.map((a) =>
            a.orderItemId === itemLine.orderItemId
                ? (a.orderDiscountAmount ?? new Decimal(0))
                : new Decimal(0),
        ),
    )
    itemLine.itemDiscountAmount = 0
    itemLine.lineCompDiscount = 0
    combosLines.forEach((i) => {
        i.orderDiscountAmount = sumDecimal(
            splitedLines.map((a) =>
                a.orderItemId === i.orderItemId
                    ? (a.orderDiscountAmount ?? new Decimal(0))
                    : new Decimal(0),
            ),
        )
        i.itemDiscountAmount = 0
        i.lineCompDiscount = 0
    })
    itemLine.modifierLines.forEach((i) => {
        i.orderDiscountAmount = sumDecimal(
            splitedLines.map((a) =>
                a.orderItemId === i.orderItemId
                    ? (a.orderDiscountAmount ?? new Decimal(0))
                    : new Decimal(0),
            ),
        )
        i.modifierLines.forEach((ml) => {
            ml.orderDiscountAmount = sumDecimal(
                splitedLines.map((a) =>
                    a.orderItemId === ml.orderItemId
                        ? (a.orderDiscountAmount ?? new Decimal(0))
                        : new Decimal(0),
                ),
            )
        })
    })
}
function finalizeOrder(
    taxLines: LineModelExtend[],
    depositLines: LineModelExtend[],
    serviceCharge: IServiceCharge,
    orderServiceChargeTax: Decimal,
    orderSubtotal: Decimal,
    totalDiscount: Decimal,
    orderServiceCharge: Decimal,
) {
    // Deposit-and-Credit Lines
    // No Tax / No Service Charge / No Discount
    const depositAndCreditSubtotal = round(
        depositLines.reduce((prev, cur) => {
            return new Decimal(cur.lineTotal ?? 0).mul(cur.amount!!).plus(prev)
        }, new Decimal(0)),
    )
    let totalInclusiveTax = taxLines.reduce(
        (prev, cur) => new Decimal(cur.taxInclusive!!).plus(prev),
        new Decimal(0),
    )

    let totalExclusiveTax = taxLines.reduce(
        (prev, cur) => new Decimal(cur.taxExclusive!!).plus(prev),
        new Decimal(0),
    )

    if (serviceCharge.isTaxInclusive) {
        totalInclusiveTax = totalInclusiveTax.plus(orderServiceChargeTax)
    } else {
        totalExclusiveTax = totalExclusiveTax.plus(orderServiceChargeTax)
    }

    const totalTax = totalExclusiveTax.plus(totalInclusiveTax)
    const totalSubTotal = orderSubtotal.plus(depositAndCreditSubtotal)

    const totalDue = round(
        totalSubTotal
            .sub(totalDiscount)
            .plus(totalExclusiveTax)
            .plus(orderServiceCharge),
    )
    return {
        totalSubTotal,
        totalDue,
        totalTax,
        totalExclusiveTax,
        totalInclusiveTax,
    }
}

function applyOrderServiceCharge(
    serviceCharge: IServiceCharge,
    itemLines: LineModelExtend[],
    entireCompDiscount: VoidCompReasonModel | undefined,
) {
    const orderServiceCharge = round(
        calculateOrderServiceCharge(
            serviceCharge,
            itemLines,
            Boolean(entireCompDiscount),
        ),
    )

    const orderServiceChargeTax = calculateTax(
        orderServiceCharge,
        serviceCharge.taxes?.find(
            (i) => i.taxID === serviceCharge.setting?.taxID,
        )?.rate,
        serviceCharge.isTaxInclusive,
    )
    return { orderServiceChargeTax, orderServiceCharge }
}
/**
 * apply order discount to each lines
 * @param itemLines
 * @param orderSubtotal
 * @param entireDiscounts
 * @param entireCompDiscount
 * @param isCalculateCompAfterDiscount
 * @returns
 */
function summarizeOrderDiscount(
    itemLines: LineModelExtend[],
    orderSubtotal: Decimal,
    entireDiscounts: DiscountContract[],
    entireCompDiscount: VoidCompReasonModel | undefined,
    isCalculateCompAfterDiscount: boolean,
) {
    const totalLineDiscount = sumDecimal(
        itemLines.map((a) =>
            new Decimal(a.itemDiscountAmount ?? 0).plus(
                a.lineCompDiscount ?? 0,
            ),
        ),
    )

    itemLines = splitOrderDiscountsToLines(
        itemLines,
        entireDiscounts,
        entireCompDiscount,
        isCalculateCompAfterDiscount,
    )
    const totalOrderDiscount = sumDecimal(
        itemLines.map((l) => l.orderDiscountAmount),
    )

    const totalDiscount = Decimal.min(
        totalLineDiscount.plus(totalOrderDiscount),
        orderSubtotal,
    )
    return { totalDiscount, itemLines }
}
export default calculateOrder
